import React, { useMemo } from 'react';
import { batch } from 'react-redux';
import type { CookieObj } from '@noths/polaris-client-gdpr-compliance';
import { acceptsFunctionalCookies } from '@noths/polaris-client-gdpr-compliance';
import { parseLocaleSetCookieHeaders } from '@noths/polaris-client-localisation';
import { isContentPreviewEnabled } from '@noths/polaris-client-next-components';
import { PreviewHeader } from '@noths/polaris-client-ribbons-design-system';
import type { SiteChromeContent } from '@noths/polaris-client-site-chrome-ui';
import { getMvtStrategyFromRequest } from '@noths/polaris-client-user-configuration';
import { generateRandomString } from '@noths/polaris-client-utils';
import type { DeepPartial } from '@noths/polaris-dev-ts-recipes';
import { type CurrencyCode, StatusCode } from '@noths/polaris-dev-ts-types';
import type { Environment } from '@noths/polaris-server-content-fetching';
import type { IncomingMessage } from 'http';
import type { GetServerSideProps } from 'next';
import getConfig from 'next/config';
import Head from 'next/head';
import type { ParsedUrlQuery } from 'querystring';
import { parse } from 'url';

import { adaptPartnerContentToStore } from 'src/adapters/contentToStore/adaptPartnerContentToStore';
import { adaptQueryStringToStore } from 'src/adapters/queryStringToStore/adaptQueryStringToStore';
import {
  adaptStoreToNextPageURL,
  adaptStoreToPreviousPageURL,
} from 'src/adapters/storeToPageQueryString/adaptStoreToPaginationURL';
import { PartnerLayout } from 'src/components/layouts/PartnerLayout/PartnerLayout';
import { ProductListError } from 'src/components/molecules/ProductListError/ProductListError';
import { PartnerListing } from 'src/components/organisms/PartnerListing/PartnerListing';
import { PartnerStory } from 'src/components/organisms/PartnerStory/PartnerStory';
import { SKIP_CONTENT_TARGET_ID } from 'src/constants/elementIds';
import { getFeatureFlagsWithMatchers } from 'src/featureFlags/getFeatureFlagsWithMatchers';
import type { ReduxApplicationState } from 'src/redux/combinedReducer/combinedReducer';
import type { CategoryContentState, PartnerContentState } from 'src/redux/content/types';
import { actions as navigationActions } from 'src/redux/navigation/slice';
import type { PartnerInsightsState } from 'src/redux/partnerInsights/types';
import { requestProducts } from 'src/redux/products/thunks/requestProducts';
import { PARTNER_NOT_FOUND_ERROR, serializeError } from 'src/redux/products/thunks/thunkErrors';
import { getInitialListingsControlsState } from 'src/redux/productsQuery/helpers/getInitialListingsControlsState';
import type { Facet } from 'src/redux/productsQuery/types/Facet';
import type { SortOption } from 'src/redux/productsQuery/types/SortOption';
import { selectPagePropsForClientHydration } from 'src/redux/selectors';
import { getStore } from 'src/redux/store';
import { actions as userActions } from 'src/redux/user/slice';
import type { BreadcrumbJSONLDSchema } from 'src/seo/linkedData/getLDBreadcrumbListSchema';
import { getLDBreadcrumbListSchema } from 'src/seo/linkedData/getLDBreadcrumbListSchema';
import { fetchGlobalContent } from 'src/services/contentstack/fetchGlobalContent';
import { fetchPartnerContent } from 'src/services/contentstack/fetchPartnerContent';
import { getContentstackEnvironment } from 'src/services/contentstack/getContentstackEnvironment';
import type { ContentstackPartner } from 'src/services/contentstack/types/PartnerPage.types';
import type { ListingsAPIServingConfig } from 'src/services/listings-api/servingConfigs';
import { getBrowseServingConfig } from 'src/services/listings-api/servingConfigs';
import { fetchDeliveryEvent } from 'src/services/mononoth/fetchDeliveryEvent';
import { fetchPartnerData } from 'src/services/mononoth/fetchPartnerData';
import { fetchPartnerInsights } from 'src/services/partner-product-insights-api/fetchPartnerInsights';
import type { PartnerProductInsightsAPIPartner } from 'src/services/partner-product-insights-api/types/PartnerProductInsightsAPIPartner';
import type { PageEventObject } from 'src/tracking/events/pageEvent';
import { getServerSidePageEventObject } from 'src/tracking/events/pageEvent';
import { PageType } from 'src/types/navigation';
import type { NextConfig } from 'src/types/nextConfig';
import type { UpdatedReduxState } from 'src/types/page';
import { getPageFromParam } from 'src/utils/queryParams';
import { logger } from 'src/utils/serverLogger';
import { urlPathToVertexPageCategory } from 'src/utils/vertex';

const {
  publicRuntimeConfig,
  serverRuntimeConfig: { CLEAR_PREVIEW_API_URL, retryAfterDuration },
} = getConfig() as NextConfig;

interface PageMetadata {
  currency: CurrencyCode;
  featureFlags: Record<string, unknown>;
  functionalCookiesConsented?: boolean;
  isPreview: boolean;
  pathname: string;
}

interface PageConfig {
  contentType: string;
  env: string;
  pageType: string;
  trackingCategory: string;
}

const pageConfig: PageConfig = {
  env: process.env.NODE_ENV,
  contentType: 'partner',
  pageType: 'partner',
  trackingCategory: 'Partner',
};

interface PartnerDataResponse {
  error?: boolean;
  partner?: {
    id: number;
    logo: string;
    name: string;
    shortcode: string;
  };
  redirect_url?: string;
}

interface PartnerPageProps extends Partial<CategoryContentState> {
  breadcrumbLinks: Array<{ rel: string; text: string }>;
  canonicalTag: string;
  metaTagDescription: string;
  pageConfig: PageConfig;
  pageEventCategory: string;
  pageEventData: PageEventObject;
  pageLiveRegionText: string;
  pageMetadata: PageMetadata;
  paginationTagValues: [string | null, string | null];
  partnerPageURL: string;
  partnerShortcode: string;
  siteChromeContent: SiteChromeContent | null;
  updatedReduxState: DeepPartial<UpdatedReduxState>;
}

const buildDefaultPageEventObject = (): PageEventObject => {
  return {
    page: {
      app_version: publicRuntimeConfig.appVersion,
      bot_caching: 'false',
      category: 'Partner',
      environment: process.env.NODE_ENV,
      filter_list: '',
      filter_nav_type: 'fixed sidebar',
      list_id: null,
      list_name: '',
      offset: 0,
      total_listing_items: 0,
      react: 'true',
      release_version: publicRuntimeConfig.version,
    },
  };
};

const updatePageEventWithBotCaching = (
  pageEvent: PageEventObject,
  req: IncomingMessage,
): PageEventObject => {
  return {
    ...pageEvent,
    page: {
      ...pageEvent.page,
      bot_caching: getMvtStrategyFromRequest(req) === 'bot' ? 'true' : 'false',
    },
  };
};

async function fetchAllPartnerPageData(
  shortcode: string,
  env: Environment,
  urlPath: string | undefined,
  req: IncomingMessage,
) {
  const featureFlags = (req && getFeatureFlagsWithMatchers(req)) || {};

  const [siteChromeContentResult, partnerData, partnerContent, partnerInsights, deliveryEvent] =
    await Promise.all([
      fetchGlobalContent({ featureFlags, urlPath: urlPath ?? '' }).catch(() => null),
      fetchPartnerData({ shortcode }).catch(() => null),
      fetchPartnerContent({ shortcode, env }).catch(() => null),
      fetchPartnerInsights(shortcode).catch(() => null),
      fetchDeliveryEvent().catch(() => null),
    ]);

  return {
    siteChromeContent: siteChromeContentResult?.[0] ?? null,
    partnerData,
    partnerContent,
    partnerInsights,
    deliveryEvent,
    featureFlags,
  };
}

const buildErrorProps = (
  partnerShortcode: string,
  siteChromeContent: SiteChromeContent | null,
  pageMetadata: PageMetadata,
  req: IncomingMessage,
  error?: Error,
): PartnerPageProps => {
  const pageEvent = buildDefaultPageEventObject();

  return {
    breadcrumbLinks: [],
    canonicalTag: '',
    metaTagDescription: '',
    pageConfig,
    pageEventCategory: pageConfig.trackingCategory,
    pageMetadata,
    pageEventData: updatePageEventWithBotCaching(pageEvent, req),
    pageLiveRegionText: '',
    paginationTagValues: [null, null],
    partnerShortcode,
    partnerPageURL: `/partners/${partnerShortcode}`,
    siteChromeContent,
    updatedReduxState: error
      ? {
          products: {
            error: serializeError(error),
          },
        }
      : {},
  };
};

const buildSuccessProps = (
  partnerShortcode: string,
  partnerData: PartnerDataResponse,
  siteChromeContent: SiteChromeContent | null,
  pageMetadata: PageMetadata,
  state: ReduxApplicationState,
): PartnerPageProps => {
  const previousPageURL = adaptStoreToPreviousPageURL(state);
  const nextPageURL = adaptStoreToNextPageURL(state);
  const partnerPageURL = `/partners/${partnerShortcode}`;
  const partnerName = partnerData?.partner?.name || partnerShortcode;
  const pageEventData = getServerSidePageEventObject(
    publicRuntimeConfig.version,
    publicRuntimeConfig.appVersion,
    state,
  );

  return {
    breadcrumbLinks: [
      { rel: '/', text: 'Homepage' },
      { rel: partnerPageURL, text: partnerName },
    ],
    canonicalTag: '',
    metaTagDescription: '',
    pageConfig,
    pageEventCategory: pageConfig.trackingCategory,
    pageMetadata,
    pageEventData,
    pageLiveRegionText: state.accessibility.statusMessage,
    paginationTagValues: [previousPageURL, nextPageURL],
    partnerShortcode,
    partnerPageURL,
    siteChromeContent,
    ...selectPagePropsForClientHydration(state),
  };
};

/**
 * Builds partner content from either adapted content or partner data
 */
const buildPartnerContent = (
  partnerContent: ContentstackPartner | null,
  partnerData: PartnerDataResponse,
) => {
  if (partnerContent) {
    return adaptPartnerContentToStore(partnerContent);
  }

  const partner = partnerData.partner;
  if (!partner) {
    throw new Error('Partner data is required when content is not from Contentstack');
  }

  return {
    id: partner.id,
    logo: {
      src: partner.logo,
      alt: partner.name,
    },
    name: partner.name,
    shortcode: partner.shortcode,
  };
};

/**
 * Transforms partner insights API response to Redux format
 */
const transformPartnerInsights = (partnerInsights: PartnerProductInsightsAPIPartner | null) => {
  if (!partnerInsights) {
    return null;
  }

  return {
    averageProductRating: partnerInsights.averageProductRating ?? null,
    numberOfSales: partnerInsights.numberOfSales ?? null,
    topBrand: partnerInsights.starBrand || false,
  };
};

/**
 * Builds the initial Redux store state
 */
const buildStoreInitialState = (
  pathname: string,
  queryParams: ParsedUrlQuery,
  partnerShortcode: string,
  partnerContent: ContentstackPartner | null,
  partnerData: PartnerDataResponse,
  partnerInsights: PartnerProductInsightsAPIPartner | null,
  facets: Facet[],
  sortOptions: SortOption[],
  servingConfig: ListingsAPIServingConfig | null,
  req: IncomingMessage,
  cognitoId: string | undefined,
  currencyCode: CurrencyCode,
  gaVisitorId: string,
) => {
  return {
    content: {
      partner: buildPartnerContent(partnerContent, partnerData),
    },
    partnerInsights: transformPartnerInsights(partnerInsights),
    navigation: {
      pageType: PageType.Partner,
    },
    productsQuery: {
      canonicalFilters: {
        and: [
          {
            key: 'attributes.partner_shortcode',
            negated: false,
            text: [partnerShortcode],
          },
        ],
      },
      facets,
      page: getPageFromParam(queryParams),
      pageCategories: [urlPathToVertexPageCategory(pathname)],
      ...(servingConfig && { servingConfig }),
      sortOptions,
    },
    user: {
      bot: getMvtStrategyFromRequest(req) === 'bot',
      cognitoID: cognitoId || null,
      currencyCode,
      gaVisitorId,
    },
  };
};

export const getServerSideProps: GetServerSideProps = async ({ query, req, res }) => {
  const preview = isContentPreviewEnabled(req);
  const env = getContentstackEnvironment(preview);
  const { shortcode, ...queryWithoutShortcode } = query;
  const queryParams = adaptQueryStringToStore(queryWithoutShortcode);
  const partnerShortcode = Array.isArray(shortcode) ? shortcode[0]! : shortcode!;

  const pathname = parse(req.url!).pathname!;
  const { _ga: googleAnalyticsVisitorId, cognitoId, currency, delivery_zone } = req.cookies;
  const { currency: initialCurrency, deliveryZone: initialDeliveryZone } =
    parseLocaleSetCookieHeaders(res.getHeaders());
  const currencyCode = (initialCurrency ?? currency) as CurrencyCode;
  const gaVisitorId = googleAnalyticsVisitorId ?? generateRandomString(10);
  const urlPath = req.url;

  const pageMetadata: PageMetadata = {
    currency: currencyCode || 'GBP',
    functionalCookiesConsented: acceptsFunctionalCookies(req.cookies as CookieObj),
    isPreview: preview,
    pathname,
    featureFlags: {},
  };

  try {
    const {
      deliveryEvent,
      featureFlags,
      partnerContent,
      partnerData,
      partnerInsights,
      siteChromeContent,
    } = await fetchAllPartnerPageData(partnerShortcode, env, urlPath, req);

    pageMetadata.featureFlags = featureFlags;

    if (!partnerData) {
      res.statusCode = StatusCode.NotFound;
      logger.error('partner not found');
      return {
        props: buildErrorProps(
          partnerShortcode,
          siteChromeContent,
          pageMetadata,
          req,
          PARTNER_NOT_FOUND_ERROR,
        ),
      };
    }

    if ('redirect_url' in partnerData) {
      return {
        redirect: {
          destination: partnerData.redirect_url,
          permanent: true,
        },
      };
    }

    const { facets, sortOptions } = getInitialListingsControlsState(queryParams, {
      deliveryEvent: deliveryEvent && {
        date: deliveryEvent.last_deliver_by,
        name: deliveryEvent.name,
      },
    });
    const servingConfig = getBrowseServingConfig(featureFlags);
    const initialState = buildStoreInitialState(
      pathname,
      queryParams,
      partnerShortcode,
      partnerContent,
      partnerData,
      partnerInsights,
      facets,
      sortOptions,
      servingConfig,
      req,
      cognitoId,
      currencyCode,
      gaVisitorId,
    );
    const { dispatch, getState } = getStore(
      {
        router: { pathname, query: queryParams },
      },
      initialState,
    );

    batch(() => {
      dispatch(navigationActions.setPathname({ url: req.url ?? '', host: req.headers.host ?? '' }));
      dispatch(userActions.setDeliveryZone(initialDeliveryZone ?? delivery_zone));
      dispatch(userActions.setFeatureFlags(featureFlags));
    });

    await dispatch(requestProducts({}));

    const state = getState();
    const { products } = state;

    if (products.error) {
      res.statusCode = StatusCode.InternalServerError;
      res.setHeader('Retry-After', retryAfterDuration);
    }

    const props = buildSuccessProps(
      partnerShortcode,
      partnerData,
      siteChromeContent,
      pageMetadata,
      state,
    );

    return {
      props,
    };
  } catch (error) {
    logger.error(`Error fetching partner products page for ${partnerShortcode}`, {
      ...(error instanceof Error
        ? { message: error.message, stack: error.stack }
        : { message: 'unknown error' }),
    });

    res.statusCode = 500;
    return {
      props: buildErrorProps(partnerShortcode, null, pageMetadata, req),
    };
  }
};

const PartnerPageHead = (props: {
  JSONLDSchema: BreadcrumbJSONLDSchema;
  canonicalTag: string;
  logoSrc: string | undefined;
  metaTagDescription: string;
  nextLinkHref: string | null;
  partnerName: string;
  partnerPageURL: string;
  prevLinkHref: string | null;
}) => (
  <Head>
    {/* In browsers title Elements can only have a single Text Node as children */}
    <title>{`${props.partnerName} | Products`}</title>
    <link href={props.canonicalTag} rel="canonical" />
    {props.prevLinkHref && <link href={props.prevLinkHref} rel="prev" />}
    {props.nextLinkHref && <link href={props.nextLinkHref} rel="next" />}
    <meta content={props.metaTagDescription} name="description" />
    <meta content={props.partnerName} property="og:title" />
    <meta content="profile" property="og:type" />
    <meta content={props.logoSrc} property="og:image" />
    <meta content={props.partnerPageURL} property="og:url" />
    <meta content="noindex" name="robots" />
    <script
      dangerouslySetInnerHTML={{ __html: JSON.stringify(props.JSONLDSchema) }}
      type="application/ld+json"
    />
  </Head>
);

const PartnerPage = React.memo((props: PartnerPageProps) => {
  const [prevLinkHref, nextLinkHref] = props.paginationTagValues || [];
  const JSONLDSchema = useMemo(
    () => getLDBreadcrumbListSchema(props.breadcrumbLinks),
    [props.breadcrumbLinks],
  );
  const partnerName = props.updatedReduxState.content?.partner?.name || props.partnerShortcode;
  const partnerContent = props.updatedReduxState.content?.partner;

  const tabs = useMemo(
    () => [
      {
        label: 'Our products',
        href: '#products',
        content: <PartnerListing stickyTopBanner={true} />,
      },
      {
        label: 'Our story',
        href: '#ourstory',
        content: partnerContent ? (
          <PartnerStory partnerContent={partnerContent as PartnerContentState} />
        ) : null,
      },
    ],
    [partnerContent],
  );

  return (
    <>
      <PartnerPageHead
        JSONLDSchema={JSONLDSchema}
        canonicalTag={props.canonicalTag}
        logoSrc={partnerContent?.logo?.src}
        metaTagDescription={props.metaTagDescription}
        nextLinkHref={nextLinkHref}
        partnerName={partnerName}
        partnerPageURL={props.partnerPageURL}
        prevLinkHref={prevLinkHref}
      />
      <div id={SKIP_CONTENT_TARGET_ID}>
        {props.pageMetadata.isPreview && <PreviewHeader href={CLEAR_PREVIEW_API_URL} />}
        {partnerContent ? (
          <PartnerLayout
            pageLiveRegionText={props.pageLiveRegionText}
            partnerContent={partnerContent as PartnerContentState}
            partnerInsights={
              (props.updatedReduxState.partnerInsights ?? undefined) as
                | PartnerInsightsState
                | undefined
            }
            tabs={tabs}
          />
        ) : (
          <ProductListError message="Partner information is currently unavailable. Please try again later." />
        )}
      </div>
    </>
  );
});

PartnerPage.displayName = 'PartnerPage';

export default PartnerPage;
